import { supabase } from '../lib/supabase';

export interface LeaderboardEntry {
  id: string;
  userId: string;
  username: string;
  avatar: string;
  score: number;
  questionsAnswered: number;
  correctAnswers: number;
  streak: number;
  timePlayed: number;
  gameMode: string;
  difficulty: string;
  measurementType?: string;
  accuracy: number;
  achievedAt: string;
}

export interface LeaderboardFilters {
  gameMode?: string;
  difficulty?: string;
  measurementType?: string;
  limit?: number;
}

export class LeaderboardService {
  /**
   * Get high scores from the past week
   */
  static async getRecentLeaderboard(filters: LeaderboardFilters = {}): Promise<LeaderboardEntry[]> {
    const {
      gameMode,
      difficulty,
      measurementType,
      limit = 50
    } = filters;

    // Calculate 1 week ago
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    try {
      // First, get the game sessions
      let query = supabase
        .from('game_sessions')
        .select(`
          id,
          user_id,
          score,
          questions_answered,
          correct_answers,
          streak,
          time_played,
          game_mode,
          difficulty,
          measurement_type,
          completed_at
        `)
        .gte('completed_at', oneWeekAgo.toISOString())
        .order('score', { ascending: false });

      // Apply filters
      if (gameMode) {
        query = query.eq('game_mode', gameMode);
      }

      if (difficulty) {
        query = query.eq('difficulty', difficulty);
      }

      if (measurementType) {
        query = query.eq('measurement_type', measurementType);
      }

      const { data: sessions, error: sessionsError } = await query.limit(limit);

      if (sessionsError) {
        console.error('Error fetching game sessions:', sessionsError);
        throw sessionsError;
      }

      if (!sessions || sessions.length === 0) {
        return [];
      }

      // Get unique user IDs from the sessions
      const userIds = [...new Set(sessions.map(session => session.user_id))];

      // Fetch profiles for these users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, avatar')
        .in('id', userIds);

      if (profilesError) {
        console.warn('Error fetching profiles, using fallback data:', profilesError);
      }

      // Create a map of user_id -> profile for easy lookup
      const profileMap = new Map();
      if (profiles) {
        profiles.forEach(profile => {
          profileMap.set(profile.id, profile);
        });
      }

      // Transform the data by combining sessions with profiles
      const transformedData = sessions.map((session: any) => {
        const profile = profileMap.get(session.user_id);
        return {
          id: session.id,
          userId: session.user_id,
          username: profile?.username || 'Anonymous User',
          avatar: profile?.avatar || '👤',
          score: session.score,
          questionsAnswered: session.questions_answered,
          correctAnswers: session.correct_answers,
          streak: session.streak,
          timePlayed: session.time_played,
          gameMode: session.game_mode,
          difficulty: session.difficulty,
          measurementType: session.measurement_type,
          accuracy: session.questions_answered > 0 
            ? Math.round((session.correct_answers / session.questions_answered) * 100)
            : 0,
          achievedAt: session.completed_at
        };
      });

      return transformedData;

    } catch (error) {
      console.error('Error in getRecentLeaderboard:', error);
      return [];
    }
  }

  /**
   * Get user's best rank and score info for a specific game mode in the past week
   */
  static async getUserRank(userId: string, gameMode?: string): Promise<{ rank: number; total: number; userScores: number; bestScore: number } | null> {
    try {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      let query = supabase
        .from('game_sessions')
        .select('user_id, score')
        .gte('completed_at', oneWeekAgo.toISOString())
        .order('score', { ascending: false });

      if (gameMode) {
        query = query.eq('game_mode', gameMode);
      }

      const { data, error } = await query;

      if (error || !data) {
        console.error('Error fetching user rank:', error);
        return null;
      }

      // Get unique players and their best scores
      const playerBestScores = new Map<string, number>();
      data.forEach(session => {
        const currentBest = playerBestScores.get(session.user_id) || 0;
        if (session.score > currentBest) {
          playerBestScores.set(session.user_id, session.score);
        }
      });

      // Sort players by their best score
      const sortedPlayers = Array.from(playerBestScores.entries())
        .sort(([,scoreA], [,scoreB]) => scoreB - scoreA);

      // Find user's position among unique players
      const userBestScore = playerBestScores.get(userId) || 0;
      const userRank = sortedPlayers.findIndex(([playerId]) => playerId === userId) + 1;
      
      // Count user's total scores in this period
      const userScores = data.filter(session => session.user_id === userId).length;
      
      return {
        rank: userRank || 0,
        total: sortedPlayers.length, // Total unique players
        userScores: userScores, // Number of scores this user has
        bestScore: userBestScore
      };

    } catch (error) {
      console.error('Error in getUserRank:', error);
      return null;
    }
  }

  /**
   * Get available game modes that have recent sessions
   */
  static async getActiveGameModes(): Promise<string[]> {
    try {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const { data, error } = await supabase
        .from('game_sessions')
        .select('game_mode')
        .gte('completed_at', oneWeekAgo.toISOString());

      if (error || !data) {
        console.error('Error fetching active game modes:', error);
        return [];
      }

      // Get unique game modes
      const uniqueModes = [...new Set(data.map(session => session.game_mode))];
      return uniqueModes;

    } catch (error) {
      console.error('Error in getActiveGameModes:', error);
      return [];
    }
  }

  /**
   * Check if user is online (has internet connection)
   */
  static isOnline(): boolean {
    return navigator.onLine;
  }
} 